export const clients = {
  addressType: {
    'establishment unit': 'Establishment unit',
    headquarters: 'Registered office',
  },
  alerts: {
    errorKboInformation: 'Something went wrong while retrieving the company details. Please try again later.',
    errorViesInformation: 'Something went wrong while retrieving the company details. Please try again later.',
    successContactCreated: 'Your contact person has been created.',
    successContactDeleted: 'Your contact person has been deleted.',
    successContactEditedOrganisation: 'Your contact person has been updated.',
    successContactEditedPrivate: 'Your client has been updated.',
    successCreated: 'Your client has been created.',
    successDeleted: 'Your client has been deleted.',
    successUpdated: 'Your client has been updated.',
  },
  breadcrumb: {
    create: 'New client',
    createContact: 'New contact person',
    edit: 'Edit client',
    editContact: 'Edit contact person',
    overview: 'Clients',
    show: 'Client details',
  },
  card: {
    address: 'Address',
    label: 'Client',
  },
  contact: 'Contact person',
  create: {
    blocker: 'Are you sure you want to leave this page? Your client has not been saved.',
    chooseType: {
      organisation: {
        description: 'Add an organization with a contact person',
        title: 'Organization',
      },
      private: {
        description: 'Add an individual client or de facto association',
        title: 'Individual',
      },
      title: 'Choose the type of client',
    },
    company: {
      changeVatNumber: 'Not what you’re looking for? Enter a different VAT number.',
      companyInformationDisclaimer:
        'We found these details based on the VAT number. You can add extra (personal) details in the next step.',
      companyInformationDisclaimerWithoutAddress:
        'We found the organization above based on the VAT number. The address of the registered office is still missing. Could you fill it in or look it up?',
      hasVatNumberLink: 'VAT number?',
      noVatNumberLink: 'No VAT number?',
      searchCompanyInformation: 'Look up',
      switchToOtherNumber: 'Doesn’t your client have a VAT number? <0>Click here to enter a different number.</0>',
      switchToVatNumber: 'Does your client have a VAT number? <0>Click here to search by VAT number.</0>',
    },
    next: 'Next',
    previous: 'Previous',
    save: 'Save',
    submit: 'Create client',
    title: 'New client',
    type: {
      contact: 'Contact person',
      organisation: 'Organization',
    },
  },
  createContact: {
    blocker: 'Are you sure you want to leave this page? Your contact person has not been saved.',
    submit: 'Add contact person',
  },
  createEdit: {
    subsections: {
      comments: {
        description: 'Keep client notes here, for example about a different payment term, special arrangements …',
        fieldFootnote: 'Only visible to you.',
        fieldLabel: 'Notes',
        title: 'Notes',
      },
      contactAddress: 'Address',
      customAgreement: {
        description:
          'Do you have an existing framework agreement with your client that lasts longer than 6 months? Tick the box. More info and why this matters to us? <a href="https://knowledge.creativeshelter.be/opdrachtgevers-en-overeenkomsten-wij-zetten-je-op-weg" target="_blank">Read it here.</a>',
        title: 'Framework agreement',
      },
      emails: {
        description: 'Choose who receives which communication, or receive it yourself.',
        title: 'Emails',
      },
      personalInfo: 'Personal details',
    },
  },
  customAgreementDialog: {
    cancel: 'No, go back',
    description:
      'Only confirm if you already have a signed agreement with this client. We’ll then send you an email to review the agreement. Why? Because we need to become a party to the agreement. More info? Read it <a href="https://knowledge.creativeshelter.be/opdrachtgevers-en-overeenkomsten-wij-zetten-je-op-weg" target="_blank">here</a>.',
    submit: 'Yes, I have a signed agreement',
    title: 'Attention!',
  },
  detail: {
    contacts: {
      add: 'Add contact person',
      columns: {
        email: 'Email address',
        name: 'Name',
        phoneNumber: 'Phone number',
      },
      delete: {
        description: 'Are you sure you want to delete contact person “{{name}}”?',
        submit: 'Delete contact person',
        title: 'Delete contact person',
      },
      moreEmails: 'And {{amount}} more',
      rowActions: {
        duplicate: 'Copy',
      },
      selfEmail: '{{email}} (you)',
      title: 'Contact persons ({{amount}})',
    },
    info: {
      creditsafe: {
        creditRating: {
          label: 'Creditworthiness',
          tooltip:
            'This score gives insight into your client’s finances.\n<faqLink href="https://knowledge.creativeshelter.be/creditsafe-wat-zegt-dat-precies-over-je-opdrachtgever">Read more about it here.</faqLink>',
        },
        score: {
          '?': {
            text: 'No information',
          },
          A: {
            text: 'Very low risk',
          },
          B: {
            text: 'Low risk',
          },
          C: {
            text: 'Average risk',
          },
          D: {
            text: 'Higher risk',
          },
        },
        status: {
          Error: 'The credit score can’t be retrieved.',
          NotFetched: 'The credit score is being retrieved.',
        },
      },
      edit: {
        action: 'Edit organization',
      },
      refreshVatInformation: {
        ariaLabel: 'Refresh company details',
        lastUpdated: 'Updated {{timeAgo}} from the CBE',
        tooltip: {
          notRefreshable:
            'Get your client’s latest details from the <kboLink>Crossroads Bank for Enterprises</kboLink>.<br/>(This is only possible if the details are more than 7 days old.)',
          refreshable: 'Get your client’s latest details from the <kboLink>Crossroads Bank for Enterprises</kboLink>.',
        },
      },
    },
    invoices: {
      title: 'Invoices',
      titleWithCount: 'Invoices ({{amount}})',
    },
  },
  editClient: {
    blocker: 'Are you sure you want to leave this page? Your changes to your client have not been saved.',
  },
  editContact: {
    blocker: 'Are you sure you want to leave this page? Your changes to your contact person have not been saved.',
  },
  fields: {
    address: {
      label: 'Address',
    },
    city: {
      label: 'City',
    },
    companyName: {
      label: 'Company name',
    },
    countryCode: {
      groups: {
        europe: 'Europe',
        europeanUnion: 'European Union',
        international: 'International',
      },
      label: 'Country',
      placeholder: 'Select a country',
    },
    customAgreement: {
      label: 'I have an existing framework agreement with this client.',
      tooltip: 'Agreements shorter than 6 months or one-off agreements don’t count.',
    },
    emailAddresses: {
      addReceiver: 'Add extra recipient',
      addSelf: 'Send it yourself',
      invoicesAndCreditNotes: 'Invoices and credit notes',
      label: 'Email address',
      paymentReminders: 'Payment reminders',
      peppolDisclaimer:
        'Your client receives invoices and credit notes via Peppol. We let them know by email that the document is available in their Peppol inbox.',
      placeholder: 'Enter the email address',
      quotations: 'Quotations',
    },
    enterpriseNumber: {
      label: 'VAT or company number',
    },
    firstName: {
      label: 'First name',
      placeholder: 'First name',
    },
    function: {
      label: 'Job title',
      placeholder: 'Job title',
    },
    lang: {
      label: 'Language',
    },
    lastName: {
      label: 'Last name',
      placeholder: 'Last name',
    },
    number: {
      label: 'House number',
    },
    organisationName: {
      add: 'De facto association',
      label: 'Organization name',
      tooltip: 'Is your client a de facto association? Enter the name of the association here.',
    },
    otherNumber: {
      label: 'Other number',
      placeholder: 'Other number',
    },
    street: {
      label: 'Street',
    },
    telephone: {
      label: 'Phone number',
      placeholder: 'Phone number',
    },
    useOrganisationAddress: {
      label: 'Use the organization’s address',
    },
    userComments: {
      placeholder: 'Note/reminder to yourself',
    },
    vatNumber: {
      label: 'VAT number',
    },
    vatOrOtherNumber: {
      label: 'VAT or other type of number',
    },
    zipcode: {
      label: 'Postal code',
    },
  },
  header: {
    actionBlock: {
      button: 'Create client',
    },
    title: 'Clients',
  },
  overview: {
    columns: {
      contacts: 'Contact persons',
      name: 'Name',
      type: 'Client type',
      vatOrOtherNumber: 'VAT or other type of number',
    },
    delete: {
      description: 'Are you sure you want to delete “{{name}}”?',
      submit: 'Delete client',
      title: 'Delete client',
    },
    moreContacts: 'And {{amount}} more',
    rowActions: {
      addContact: 'Add contact person',
      view: 'View',
    },
    vat: {
      mailTooltip: 'This company isn’t connected to the Peppol network yet. That’s why we send the invoice by email.',
      peppolTooltip: 'This company is connected to the Peppol network. Invoices and credit notes are sent via Peppol.',
    },
  },
  peppol: {
    status: {
      active: 'Active',
      error: 'We couldn’t retrieve your client’s Peppol ID.',
      label: 'Peppol',
      notRegistered: 'Not yet registered on Peppol',
      pending: 'Retrieving Peppol ID…',
      tooltip: {
        active:
          "Is your client active on Peppol? Then invoices and credit notes are sent automatically via the Peppol network. Read <faqLink href='https://www.creativeshelter.be/knowledge-hub/sturen-jullie-facturen-via-peppol-mercuriusplatform-e-invoicing'>all about it here</faqLink>.",
        notRegistered: 'Your client isn’t registered on Peppol yet. That’s why we send invoices by email for now.',
      },
    },
  },
  type: {
    organisation: 'Organization',
    private: 'Individual',
  },
};
